import { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, PenTool, Code2, Rocket, CheckCircle, ArrowRight } from 'lucide-react'

const ProcessSection = () => {
    const [currentStep, setCurrentStep] = useState(0)
    const [progress, setProgress] = useState(0)
    const intervalRef = useRef(null)

    const processSteps = [
        { 
            step: "01", 
            title: "Discovery", 
            desc: "Deep dive into your business goals, challenges, and requirements to lay a solid foundation.",
            icon: Search
        },
        { 
            step: "02", 
            title: "Planning", 
            desc: "Strategic roadmap with clear milestones, technology selection, and resource allocation.",
            icon: PenTool
        },
        { 
            step: "03", 
            title: "Development", 
            desc: "Agile sprints, continuous testing, and regular feedback loops to ensure quality.",
            icon: Code2
        },
        { 
            step: "04", 
            title: "Launch & Support", 
            desc: "Smooth deployment, user training, and ongoing maintenance for long-term success.",
            icon: Rocket
        }
    ]

    useEffect(() => {
        startAutoRotation()
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [])

    const startAutoRotation = () => {
        if (intervalRef.current) clearInterval(intervalRef.current)
        intervalRef.current = setInterval(() => {
            setCurrentStep((prev) => (prev + 1) % processSteps.length)
            setProgress(0)
        }, 4500)
    }

    useEffect(() => {
        const timer = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) return 100
                return prev + 1.5
            })
        }, 60)
        return () => clearInterval(timer)
    }, [currentStep])

    const handleStepClick = (index) => {
        if (index === currentStep) return
        setCurrentStep(index)
        setProgress(0)
        startAutoRotation()
    }

    const activeStep = processSteps[currentStep]
    const Icon = activeStep.icon

    // Calculate progress width as percentage (0% to 100%)
    const totalSteps = processSteps.length
    const baseProgress = (currentStep / (totalSteps - 1)) * 100
    const stepProgress = (progress / 100) * (100 / (totalSteps - 1))
    const totalWidth = Math.min(baseProgress + stepProgress, 100)

    return (
        <div className="py-24 bg-background transition-colors duration-300 relative overflow-hidden">
            {/* Subtle background glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

            <div className="max-w-6xl mx-auto px-4 relative z-10">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <span className="text-primary text-xs font-bold uppercase tracking-widest font-secondary mb-2 block">
                        How We Work
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold font-heading mb-4">Our Process</h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
                </div>

                {/* Step Indicators - Always visible line on desktop */}
                <div className="relative mb-16 max-w-4xl mx-auto">
                    {/* Background Line */}
                    <div className="absolute top-[45%] left-0 right-0 h-1 bg-muted -translate-y-1/2 rounded-full hidden md:block" />
                    
                    {/* Animated Progress Line */}
                    <motion.div 
                        className="absolute top-[45%] left-0 h-1 bg-gradient-to-r from-primary to-accent -translate-y-1/2 rounded-full hidden md:block"
                        style={{ width: `${totalWidth}%` }}
                        transition={{ duration: 0.1, ease: "linear" }}
                    />
                    
                    {/* Steps */}
                    <div className="relative flex justify-between items-start">
                        {processSteps.map((step, idx) => {
                            const isActive = currentStep === idx
                            const isPast = currentStep > idx
                            
                            return (
                                <div key={idx} className="flex flex-col items-center flex-1">
                                    <motion.button
                                        onClick={() => handleStepClick(idx)}
                                        className={`relative z-10 w-12 h-12 md:w-16 md:h-16 rounded-2xl flex items-center justify-center transition-all duration-500 border-2 ${
                                            isActive 
                                                ? 'bg-gradient-to-br from-primary to-accent border-transparent scale-110 shadow-[0_10px_30px_rgba(14,165,233,0.3)]' 
                                                : isPast 
                                                    ? 'bg-card border-primary text-primary' 
                                                    : 'bg-card border-border text-muted-foreground hover:border-primary/50'
                                        }`}
                                        whileHover={{ scale: isActive ? 1.1 : 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {isActive ? (
                                            <step.icon className="w-5 h-5 md:w-7 md:h-7 text-primary-foreground drop-shadow-sm" />
                                        ) : isPast ? (
                                            <CheckCircle className="w-5 h-5 md:w-7 md:h-7 text-primary" />
                                        ) : (
                                            <step.icon className="w-5 h-5 md:w-6 md:h-6" />
                                        )}
                                        
                                        {/* Glow pulse for active step */}
                                        {isActive && (
                                            <motion.div 
                                                animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0, 0] }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                                className="absolute inset-0 bg-primary rounded-2xl -z-10"
                                            />
                                        )}
                                    </motion.button>
                                    
                                    <span className={`text-[11px] md:text-sm font-bold mt-4 text-center transition-colors font-heading uppercase tracking-wider ${
                                        isActive ? 'text-foreground' : isPast ? 'text-foreground/80' : 'text-muted-foreground'
                                    }`}>
                                        {step.title}
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Active Step Card */}
                <div className="h-[180px] sm:h-[150px]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentStep}
                            initial={{ opacity: 0, scale: 0.95, filter: 'blur(5px)' }}
                            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                            exit={{ opacity: 0, scale: 0.95, filter: 'blur(5px)' }}
                            transition={{ duration: 0.4, type: "spring", stiffness: 100 }}
                            className="bg-card/80 backdrop-blur-md rounded-3xl p-6 md:p-8 shadow-sm border border-border max-w-2xl mx-auto"
                        >
                            <div className="flex items-start sm:items-center gap-5 sm:gap-6">
                                <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex flex-shrink-0 items-center justify-center">
                                    <Icon className="w-7 h-7 text-primary drop-shadow-sm" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                                        <span className="text-sm font-black text-primary font-heading">
                                            {activeStep.step}
                                        </span>
                                        <span className="text-xl font-bold font-heading text-card-foreground">
                                            {activeStep.title}
                                        </span>
                                    </div>
                                    <p className="text-muted-foreground text-sm leading-relaxed font-body">
                                        {activeStep.desc}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Navigation Dots */}
                <div className="flex justify-center gap-2 mt-8">
                    {processSteps.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => handleStepClick(idx)}
                            className={`transition-all duration-500 rounded-full ${
                                currentStep === idx 
                                    ? 'w-8 h-1.5 bg-primary' 
                                    : 'w-2 h-1.5 bg-border hover:bg-muted-foreground/50'
                            }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}

export default ProcessSection